import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Loader2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "outline";
type Size = "sm" | "md" | "lg";

const VARIANT: Record<Variant, string> = {
  primary: "bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-indigo-300",
  secondary: "bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40",
  ghost: "bg-transparent text-gray-500 hover:bg-gray-50 hover:text-indigo-600",
  danger: "bg-rose-600 text-white hover:bg-rose-700 disabled:bg-rose-300",
  outline:
    "bg-white text-gray-600 border border-gray-200 hover:border-indigo-300 hover:text-indigo-600",
};

const SIZE: Record<Size, string> = {
  sm: "px-3 py-1.5 text-[9px]",
  md: "px-5 py-2.5 text-[10px]",
  lg: "px-6 py-3 text-xs",
};

const BASE =
  "inline-flex items-center justify-center gap-2 rounded-xl font-black uppercase tracking-[0.2em] transition-all disabled:cursor-not-allowed";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: LucideIcon;
  iconPosition?: "left" | "right";
  loading?: boolean;
  fullWidth?: boolean;
  children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    icon: Icon,
    iconPosition = "left",
    loading = false,
    fullWidth = false,
    disabled,
    className,
    children,
    type = "button",
    ...props
  },
  ref,
) {
  const iconSize = size === "sm" ? 12 : size === "lg" ? 16 : 14;
  const iconNode = loading ? (
    <Loader2 size={iconSize} className="animate-spin" />
  ) : Icon ? (
    <Icon size={iconSize} />
  ) : null;

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(BASE, VARIANT[variant], SIZE[size], fullWidth && "w-full", className)}
      {...props}
    >
      {iconPosition === "left" && iconNode}
      {children}
      {iconPosition === "right" && iconNode}
    </button>
  );
});
